import {
  useUpdatePassword,
  useUpdateUser,
  useUpdateUserSettings,
} from 'features/user';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Locales } from 'shared/assets/localization';
import { ChangePasswordDialog } from 'shared/dialogs/change-password';
import { UserSettingsDialog } from 'shared/dialogs/user-settings';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { useToggle } from 'shared/hooks/use-toggle';
import { useUser } from 'shared/hooks/use-user';
import {
  DateFormatOptions,
  initialDateFormat,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { initialThemeMode, ThemeModes } from 'shared/types/theme-modes';
import { Notifications } from 'shared/types/user-settings';
import { initialWeekStartsOn, WeekDays } from 'shared/types/week-days';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { targetIsInput } from 'shared/utils/target-is-input';

import {
  UserSettingsContext,
  UserSettingsContextProps,
} from './user-settings-context';

export type UserSettingsProviderProps = {
  children: React.ReactNode;
};

export const UserSettingsProvider: React.FC<UserSettingsProviderProps> = ({
  children,
}) => {
  const [dialogOpen, toggleDialog, forceDialog] = useToggle(false);
  const [passwordDialogOpen, togglePasswordDialog, forcePasswordDialog] =
    useToggle(false);
  const user = useUser();
  const { submit: updateUser } = useUpdateUser();
  const updateUserSettings = useUpdateUserSettings();
  const {
    updatePassword,
    isLoading: passwordLoading,
    error: passwordError,
    isSuccess: passwordSuccess,
  } = useUpdatePassword();

  const [updateUserName] = useDelayedCallback(
    useCallback(
      (name: string) => !!name && updateUser({ name }, user),
      [updateUser, user],
    ),
    1000,
  );

  const updateUserLanguage = useCallback(
    (language: Locales) => updateUser({ language }, user),
    [updateUser, user],
  );

  const updateUserDateFormat = useCallback(
    (dateFormat: DateFormatOptions) => updateUser({ dateFormat }, user),
    [updateUser, user],
  );

  const updateUserTimeFormat = useCallback(
    (timeFormat: TimeFormatOptions) => updateUserSettings({ timeFormat }),
    [updateUserSettings],
  );

  const updateWeekStart = useCallback(
    (weekDay: WeekDays) => updateUser({ startOfWeek: weekDay }, user),
    [updateUser, user],
  );

  const updateNotifications = useCallback(
    (notifications: Notifications) => updateUser({ notifications }, user),
    [updateUser, user],
  );

  const updateUserThemePreference = useCallback(
    (themePreference: ThemeModes) => updateUser({ themePreference }, user),
    [updateUser, user],
  );

  const value = useMemo<UserSettingsContextProps>(
    () => ({
      openSettings: toggleDialog,
      openChangePassword: togglePasswordDialog,
    }),
    [toggleDialog, togglePasswordDialog],
  );

  useEffect(() => {
    if (passwordSuccess) {
      forcePasswordDialog(false);
    }
  }, [forcePasswordDialog, passwordSuccess]);

  useModifierKeysDown('Comma', (e) => {
    if (targetIsInput(e.target)) {
      return; // Don't open dialog
    }
    forceDialog(true);
    e.preventDefault();
  });

  return (
    <UserSettingsContext.Provider value={value}>
      {children}
      {!!user && (
        <>
          <UserSettingsDialog
            open={dialogOpen}
            onClose={toggleDialog}
            userName={user.name}
            updateUserName={updateUserName}
            language={user.settings?.language}
            onChangeLanguage={updateUserLanguage}
            email={user.email}
            isSignedInWithProvider={user.isSiwa}
            dateFormat={user.settings?.dateFormat ?? initialDateFormat}
            onChangeDateFormat={updateUserDateFormat}
            timeFormat={getTimeFormat(user)}
            onChangeTimeFormat={updateUserTimeFormat}
            weekStartSetOnIos={
              user.settings?.startOfWeekIsIosSystemManaged ?? false
            }
            weekStartsOn={user.settings?.startOfWeek ?? initialWeekStartsOn}
            onChangeWeekStart={updateWeekStart}
            onChangePassword={togglePasswordDialog}
            notifications={user.settings?.notifications}
            onChangeNotifications={updateNotifications}
            themePreference={
              user.settings?.themePreference?.desktop ?? initialThemeMode
            }
            onChangeThemePreference={updateUserThemePreference}
          />
          <ChangePasswordDialog
            open={passwordDialogOpen}
            onSubmit={updatePassword}
            error={passwordError ?? undefined}
            isLoading={passwordLoading}
            onClose={togglePasswordDialog}
          />
        </>
      )}
    </UserSettingsContext.Provider>
  );
};
